import { useEffect, useRef } from "react";

// Measures the time a player actually spends playing, sent with endSession as
// the session's active_ms. A game starts the clock when play begins and stops
// it at the last answer; pause() / resume() cover an in-game pause, and time
// while the tab is hidden never counts.
export const createPlayClock = (now = () => Date.now()) => {
  let total = 0; // ms banked from finished stretches
  let since = null; // when the current stretch began; null while not ticking
  let started = false;
  let running = false; // between start() and stop()
  let paused = false; // the game's own pause
  let hidden = false; // the tab is hidden

  // Bank the stretch so far, then begin a new one if the clock should tick.
  const sync = () => {
    const t = now();
    if (since !== null) total += t - since;
    since = running && !paused && !hidden ? t : null;
  };

  // Play time so far in whole ms (frozen once stopped); null before start().
  const elapsedMs = () => {
    if (!started) return null;
    const live = since !== null ? now() - since : 0;
    return Math.round(total + live);
  };

  return {
    // Begin measuring; on a restart this starts again from zero.
    start() {
      total = 0;
      since = null;
      started = true;
      running = true;
      paused = false;
      sync();
    },
    pause() {
      paused = true;
      sync();
    },
    resume() {
      paused = false;
      sync();
    },
    // Stop measuring and return the play time in ms (see elapsedMs).
    stop() {
      running = false;
      sync();
      return elapsedMs();
    },
    elapsedMs,
    setHidden(isHidden) {
      hidden = isHidden;
      sync();
    },
  };
};

// One play clock for the life of the component, paused while the tab is hidden.
export const usePlayClock = () => {
  const clockRef = useRef(null);
  if (clockRef.current === null) clockRef.current = createPlayClock();

  useEffect(() => {
    const clock = clockRef.current;
    const onVisibilityChange = () => clock.setHidden(document.hidden);
    onVisibilityChange();
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);

  return clockRef.current;
};
